import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { showToast } from './toastSlice';
import getAPIbaseURL from "../../utils/helpers";
import i18n from "../../i18n";
import { clearSessionStorage, getStoredToken } from "../../utils/session";
import { setTimeFormatPreference } from "../../utils/timeFormat";

const API_URL = `${getAPIbaseURL()}/users`;

export const login = createAsyncThunk(
  "auth/login",
  async (userData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/login`, userData);
      localStorage.setItem("token", response.data.access_token);

      dispatch(showToast({ message: i18n.t("slices.authSlice.login_success"), type: "success" }));
      return response.data;
    } catch (error) {
      const backendCode = error.response?.data?.detail || "generic_error";
      
      const translationKey = `slices.authSlice.errors.${backendCode.toLowerCase()}`;
      const toastMessage = i18n.exists(translationKey) 
        ? i18n.t(translationKey) 
        : i18n.t("slices.authSlice.errors.generic_error");

      dispatch(showToast({ message: toastMessage, type: "error" }));
      return rejectWithValue(backendCode);
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser', 
  async (_, thunkAPI) => {
    try {
      const token = getStoredToken();
      if (!token) throw new Error("token_not_found");

      const response = await axios.get(`${API_URL}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      clearSessionStorage();
      return thunkAPI.rejectWithValue(error.response?.data?.detail || "fetch_user_failed");
    }
});

export const logoutUser = createAsyncThunk(
  'auth/logoutUser', 
  async (_, { dispatch }) => {
    const idToken = localStorage.getItem('keycloak_id_token');
    const token = localStorage.getItem('token');

    clearSessionStorage();
    dispatch(logout());

    if (idToken) {
      try {
        const response = await axios.get(`${getAPIbaseURL()}/auth/sso/logout`, {
          params: { id_token: idToken },
          headers: { Authorization: `Bearer ${token}` }
        });

        window.location.href = response.data.logout_url;
        return;
      } catch (error) {
        console.error("SSO Logout failed", error);
      }
    }

    window.location.href = "/login";
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: getStoredToken(),
    isAuthenticated: !!getStoredToken(),
    isLoading: !!getStoredToken(),
    error: null,
  },

  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      clearSessionStorage();
    },
    setToken(state, action) {
      state.token = action.payload;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.access_token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        setTimeFormatPreference(action.payload.user?.preferences?.time_format);
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;
        state.isLoading = false;
        setTimeFormatPreference(action.payload.preferences?.time_format);
      })
      .addCase(getCurrentUser.rejected, (state, _action) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.isLoading = false;
      })
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { logout, setToken } = authSlice.actions;
export default authSlice.reducer;
